/**
 * Единые правила показа чисел, денег и дат. До этого каждый модуль форматировал
 * по-своему: лояльность печатала тиыны как есть («Баланс 240100» вместо 2 401 ₸),
 * а таблица планов показывала тенге под заголовком «Цена, тиын» — расхождение
 * в сто раз на одном экране.
 */

export const MINOR_IN_MAJOR = 100;

export function formatMoney(minor: number | null | undefined): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) return '—';
  const major = minor / MINOR_IN_MAJOR;
  return `${major.toLocaleString('ru-RU', {
    minimumFractionDigits: major % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })} ₸`;
}

/** Тенге из поля ввода → тиыны для сервера. Пустая строка даёт null, а не 0. */
export function minorFromInput(value: string): number | null {
  const normalized = value.replace(/\s/g, '').replace(',', '.');
  if (!normalized) return null;
  const major = Number(normalized);
  if (!Number.isFinite(major)) return null;
  return Math.round(major * MINOR_IN_MAJOR);
}

/** Тиыны → строка для поля ввода в тенге (без символа валюты). */
export function inputFromMinor(minor: number | null | undefined): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) return '';
  const major = minor / MINOR_IN_MAJOR;
  return major % 1 === 0 ? String(major) : major.toFixed(2);
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

export function formatShare(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(digits).replace('.', ',')} %`;
}

/**
 * «4 строк» вместо «4 строки» — мелочь, но она на каждом экране выгрузок.
 */
export function plural(count: number, forms: readonly [string, string, string]): string {
  const abs = Math.abs(count) % 100;
  const tail = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (tail > 1 && tail < 5) return forms[1];
  if (tail === 1) return forms[0];
  return forms[2];
}

export function countWithNoun(
  count: number,
  forms: readonly [string, string, string],
): string {
  return `${formatNumber(count)} ${plural(count, forms)}`;
}

const DATE_TIME = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const DATE_ONLY = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export function formatMoment(iso: string | null | undefined): string {
  if (!iso) return '—';
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? '—' : DATE_TIME.format(parsed);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? '—' : DATE_ONLY.format(parsed);
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} мс`;
  const seconds = ms / 1000;
  if (seconds < 90) return `${seconds.toFixed(seconds < 10 ? 1 : 0)} с`;
  const minutes = seconds / 60;
  if (minutes < 90) return `${minutes.toFixed(0)} мин`;
  return `${(minutes / 60).toFixed(1)} ч`;
}

/** Короткий вид идентификатора: последние 8 символов ULID читаются, все 26 — нет. */
export function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.length > 10 ? `…${id.slice(-8)}` : id;
}
